import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, FolderKanban, ListTodo, Inbox, Receipt, LineChart, Target,
  LogOut, Leaf, Gift, StickyNote, FileText, Bell, Check, ShieldCheck, Settings as SettingsIcon,
  MoreHorizontal,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import OnboardingTour from "@/components/OnboardingTour";
import InstallAppButton from "@/components/InstallAppButton";
import ThemeToggle from "@/components/ThemeToggle";
import SupportBanner from "@/components/SupportBanner";

const NAV = [
  { to: "/dashboard", icon: LayoutDashboard, label: "Dashboard", tid: "nav-dashboard" },
  { to: "/inbox", icon: Inbox, label: "Inbox", tid: "nav-inbox" },
  { to: "/clienti", icon: Users, label: "Clienti", tid: "nav-clienti" },
  { to: "/progetti", icon: FolderKanban, label: "Progetti", tid: "nav-progetti" },
  { to: "/attivita", icon: ListTodo, label: "Attività", tid: "nav-attivita" },
  { to: "/appunti", icon: StickyNote, label: "Appunti", tid: "nav-appunti" },
  { to: "/preventivi", icon: FileText, label: "Preventivi", tid: "nav-preventivi" },
  { to: "/fatture", icon: Receipt, label: "Fatture", tid: "nav-fatture" },
  { to: "/report", icon: LineChart, label: "Report", tid: "nav-report" },
  { to: "/obiettivi", icon: Target, label: "Obiettivi", tid: "nav-obiettivi" },
  { to: "/invita", icon: Gift, label: "Invita amici", tid: "nav-invita" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [notif, setNotif] = useState({ items: [], unread: 0 });

  useEffect(() => {
    api.get("/subscription/status").then(({ data }) => setSub(data)).catch(() => {});
    loadNotif();
    const t = setInterval(loadNotif, 60_000);
    return () => clearInterval(t);
  }, []);

  const loadNotif = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotif(data);
    } catch { /* silent */ }
  };
  const markRead = async (nid) => {
    await api.put(`/notifications/${nid}/read`);
    loadNotif();
  };
  const markAll = async () => {
    await api.put("/notifications/read-all");
    loadNotif();
  };

  const initials = (user?.name || user?.email || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  const onLogout = async () => {
    await logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen bg-zen-bg flex" data-testid="app-shell">
      {/* Sidebar — sticky full-viewport height */}
      <aside className="w-64 shrink-0 hidden md:flex flex-col border-r border-zen-border bg-white md:sticky md:top-0 md:h-screen md:self-start">
        <div className="px-5 py-6 flex items-center shrink-0">
          <img
            src="/brand/freelanzen-wordmark.png"
            alt="FreelanZen"
            className="h-7 w-auto object-contain block dark:hidden"
          />
          <img
            src="/brand/freelanzen-wordmark-dark.png"
            alt="FreelanZen"
            className="h-7 w-auto object-contain hidden dark:block"
          />
        </div>
        <nav className="flex-1 min-h-0 overflow-y-auto px-3 py-2 space-y-1">
          {NAV.map((it) => (
            <NavLink
              key={it.to} to={it.to} data-testid={it.tid}
              className={({ isActive }) => `zen-sidebar-link ${isActive ? "active" : ""}`}
            >
              <it.icon size={18} strokeWidth={1.5} />
              <span className="text-sm font-medium">{it.label}</span>
            </NavLink>
          ))}
          {user?.is_admin && (
            <NavLink
              to="/admin" data-testid="nav-admin"
              className={({ isActive }) => `zen-sidebar-link ${isActive ? "active" : ""}`}
            >
              <ShieldCheck size={18} strokeWidth={1.5}/>
              <span className="text-sm font-medium">Admin</span>
            </NavLink>
          )}
          <NavLink
            to="/impostazioni" data-testid="nav-settings"
            className={({ isActive }) => `zen-sidebar-link ${isActive ? "active" : ""}`}
          >
            <SettingsIcon size={18} strokeWidth={1.5}/>
            <span className="text-sm font-medium">Impostazioni Azienda</span>
          </NavLink>
        </nav>
        <div className="p-4 border-t border-zen-border space-y-3 shrink-0">
          {sub && (sub.status === "trial" || sub.status === "expired" || sub.status === "past_due" || sub.status === "cancelled") && (
            <button
              onClick={()=>navigate("/piano")}
              data-testid="trial-banner"
              className={`w-full text-left p-3 rounded-xl text-sm transition-colors ${sub.status === "trial" ? "bg-zen-primary/10 hover:bg-zen-primary/15 text-zen-ink" : "bg-zen-alert/10 hover:bg-zen-alert/15 text-zen-ink"}`}
            >
              {sub.status === "trial" ? (
                <>
                  <div className="font-medium">🎁 Prova gratuita</div>
                  <div className="text-xs text-zen-ink2 mt-0.5">
                    <span data-testid="trial-days-badge">{sub.trial_days_left}</span> giorni rimasti · <span className="text-zen-primary font-medium">Passa a Premium</span>
                  </div>
                </>
              ) : (
                <>
                  <div className="font-medium text-zen-alert">⚠️ Piano scaduto</div>
                  <div className="text-xs text-zen-ink2 mt-0.5">Riattiva Premium — € 9,99/mese</div>
                </>
              )}
            </button>
          )}
          {sub?.status === "active" && (
            <button
              onClick={()=>navigate("/piano")}
              data-testid="premium-badge"
              className="w-full text-left p-3 rounded-xl bg-zen-primary/10 hover:bg-zen-primary/15 text-sm transition-colors"
            >
              <div className="font-medium text-zen-primary">✓ Premium attivo</div>
              <div className="text-xs text-zen-ink2 mt-0.5">Gestisci abbonamento</div>
            </button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="user-menu-trigger" className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-zen-bg transition-colors">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={user?.picture || undefined} />
                  <AvatarFallback className="bg-zen-primary/10 text-zen-primary text-sm">{initials}</AvatarFallback>
                </Avatar>
                <div className="text-left flex-1 min-w-0">
                  <div className="text-sm font-medium text-zen-ink truncate">{user?.name}</div>
                  <div className="text-xs text-zen-ink2 truncate">{user?.email}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={onLogout} data-testid="logout-item" className="text-zen-alert focus:text-zen-alert">
                <LogOut size={16} className="mr-2" /> Esci
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 inset-x-0 z-20 bg-white border-b border-zen-border px-4 py-3 flex items-center justify-between">
        <img
          src="/brand/freelanzen-wordmark.png"
          alt="FreelanZen"
          className="h-6 w-auto object-contain"
        />
        <Button variant="ghost" size="sm" onClick={onLogout} data-testid="mobile-logout"><LogOut size={16}/></Button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0 md:pt-0 pt-14">
        {/* Top bar with install app + theme toggle + notifications bell */}
        <div className="hidden md:flex sticky top-0 z-10 bg-zen-bg/80 backdrop-blur-sm items-center justify-end gap-2 px-10 pt-4 pb-2">
          <InstallAppButton />
          <ThemeToggle />
          <Popover>
            <PopoverTrigger asChild>
              <button
                data-testid="notif-bell"
                className="relative w-10 h-10 rounded-full bg-white border border-zen-border hover:bg-zen-subtle/20 flex items-center justify-center transition-colors"
                title="Notifiche"
              >
                <Bell size={17} strokeWidth={1.6}/>
                {notif.unread > 0 && (
                  <span
                    data-testid="notif-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-zen-alert text-white text-[10px] font-medium flex items-center justify-center"
                  >
                    {notif.unread > 9 ? "9+" : notif.unread}
                  </span>
                )}
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-96 p-0 max-h-[520px] overflow-hidden flex flex-col" data-testid="notif-panel">
              <div className="p-4 border-b border-zen-border flex items-center justify-between">
                <div>
                  <div className="font-heading text-base">Notifiche</div>
                  <div className="text-xs text-zen-ink2">{notif.unread} non lette</div>
                </div>
                {notif.unread > 0 && (
                  <Button data-testid="notif-mark-all" size="sm" variant="ghost" onClick={markAll} className="text-xs">
                    <Check size={13} className="mr-1"/> Segna lette
                  </Button>
                )}
              </div>
              <div className="flex-1 overflow-y-auto">
                {notif.items.length === 0 ? (
                  <div className="p-8 text-center text-sm text-zen-ink2">
                    Nessuna notifica. Torna domani mattina 🌱
                  </div>
                ) : notif.items.map((n) => (
                  <button
                    key={n.notification_id}
                    data-testid={`notif-${n.notification_id}`}
                    onClick={() => { markRead(n.notification_id); if (n.link) navigate(n.link); }}
                    className={`w-full text-left p-4 border-b border-zen-border hover:bg-zen-bg transition-colors ${!n.read ? "bg-zen-primary/5" : ""}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="font-medium text-sm text-zen-ink">{n.title}</div>
                      {!n.read && <div className="w-2 h-2 rounded-full bg-zen-primary shrink-0 mt-1.5"/>}
                    </div>
                    <div className="text-xs text-zen-ink2 mt-1">{n.body}</div>
                    <div className="text-[10px] text-zen-ink2 mt-1.5">
                      {n.created_at ? new Date(n.created_at).toLocaleString("it-IT") : ""}
                    </div>
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
        <div className="max-w-7xl mx-auto px-6 md:px-10 pb-10 md:pt-4">
          <Outlet />
        </div>
        <SupportBanner variant="app" />
        <OnboardingTour />

        {/* Mobile bottom nav — 5 primary items + "Altro" popover with the rest */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-zen-border grid grid-cols-6 z-20">
          {NAV.slice(0, 5).map((it) => (
            <NavLink key={it.to} to={it.to} data-testid={`m-${it.tid}`}
              className={({ isActive }) => `py-3 flex flex-col items-center gap-1 text-xs ${isActive ? "text-zen-primary" : "text-zen-ink2"}`}>
              <it.icon size={18} strokeWidth={1.5}/>
              <span>{it.label}</span>
            </NavLink>
          ))}
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                data-testid="m-nav-altro"
                className="py-3 flex flex-col items-center gap-1 text-xs text-zen-ink2 hover:text-zen-primary transition-colors"
              >
                <MoreHorizontal size={18} strokeWidth={1.5}/>
                <span>Altro</span>
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="end" side="top" sideOffset={8}
              className="w-64 p-2"
              data-testid="m-nav-altro-panel"
            >
              {NAV.slice(5).map((it) => (
                <NavLink
                  key={it.to} to={it.to} data-testid={`m-${it.tid}`}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${isActive ? "bg-zen-primary/10 text-zen-primary" : "text-zen-ink hover:bg-zen-bg"}`}
                >
                  <it.icon size={16} strokeWidth={1.5}/>
                  <span>{it.label}</span>
                </NavLink>
              ))}
              {user?.is_admin && (
                <NavLink
                  to="/admin" data-testid="m-nav-admin"
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${isActive ? "bg-zen-primary/10 text-zen-primary" : "text-zen-ink hover:bg-zen-bg"}`}
                >
                  <ShieldCheck size={16} strokeWidth={1.5}/>
                  <span>Admin</span>
                </NavLink>
              )}
              <div className="h-px bg-zen-border my-1"/>
              <NavLink
                to="/impostazioni" data-testid="m-nav-settings"
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${isActive ? "bg-zen-primary/10 text-zen-primary" : "text-zen-ink hover:bg-zen-bg"}`}
              >
                <SettingsIcon size={16} strokeWidth={1.5}/>
                <span>Impostazioni Azienda</span>
              </NavLink>
            </PopoverContent>
          </Popover>
        </nav>
      </main>
    </div>
  );
}
